// RITA-1525 (Teilpaket 2) — Eigener Verlauf für Rückgängig/Wiederholen.
//
// Der Browser-Verlauf ist ungeeignet, weil das DOM nie direkt bearbeitet wird: Jede Eingabe erzeugt
// ein neues, unveränderliches Dokument. Der Verlauf speichert Dokument und Auswahl vor einer
// Änderung; der Aufrufer kann über den Typparameter weitere Felder mitgeben, die unverändert
// zurückkommen (der Editor: die gemerkte Textstelle). Zusammenhängendes Tippen oder Löschen an
// derselben Stelle innerhalb kurzer Zeit wird zu einem Schritt zusammengefasst, damit „Rückgängig“
// ein Wort statt eines Zeichens zurücknimmt.
import { Auswahl, auswahlenGleich } from './positionen';
import { Vertragsdokument } from './types';

export interface Zustand {
  dokument: Vertragsdokument;
  auswahl: Auswahl | null;
}

export type Schrittart = 'tippen' | 'loeschen' | 'sonstiges';

export interface LetzterSchritt {
  art: Schrittart;
  zeitMs: number;
  /** Auswahl nach dem Schritt; nur wenn der nächste Schritt genau dort weitergeht, wird zusammengefasst. */
  auswahlNachher: Auswahl | null;
}

export interface Verlauf<Z extends Zustand = Zustand> {
  vergangenheit: Z[];
  zukunft: Z[];
  letzter: LetzterSchritt | null;
}

export const ZUSAMMENFASSEN_MS = 1000;
export const MAX_SCHRITTE = 200;

export function leererVerlauf<Z extends Zustand = Zustand>(): Verlauf<Z> {
  return { vergangenheit: [], zukunft: [], letzter: null };
}

/**
 * Nimmt den Zustand VOR einer Änderung auf. Gleiche Art (Tippen bzw. Löschen), höchstens
 * ZUSAMMENFASSEN_MS seit dem letzten Schritt und dieselbe Stelle (Auswahl vorher = Auswahl nach dem
 * letzten Schritt) werden mit dem letzten Schritt zusammengefasst: der älteste Vorher-Zustand bleibt.
 * `mitLetztemVerschmelzen` erzwingt das unabhängig von Art, Zeit und Stelle: für eine Änderung, die
 * fachlich zum vorherigen Schritt gehört (Komposition über einer Auswahl: Löschen + Einfügen).
 */
export function aufnehmen<Z extends Zustand>(
  verlauf: Verlauf<Z>,
  vorher: Z,
  art: Schrittart,
  zeitMs: number,
  auswahlNachher: Auswahl | null,
  mitLetztemVerschmelzen = false,
): Verlauf<Z> {
  const { letzter } = verlauf;
  const anschluss = letzter !== null && verlauf.vergangenheit.length > 0;
  const zusammenfassen =
    anschluss &&
    (mitLetztemVerschmelzen ||
      (art !== 'sonstiges' &&
        letzter.art === art &&
        zeitMs - letzter.zeitMs <= ZUSAMMENFASSEN_MS &&
        auswahlenGleich(letzter.auswahlNachher, vorher.auswahl)));
  const vergangenheit = zusammenfassen
    ? verlauf.vergangenheit
    : [...verlauf.vergangenheit, vorher].slice(-MAX_SCHRITTE);
  return { vergangenheit, zukunft: [], letzter: { art, zeitMs, auswahlNachher } };
}

export function kannRueckgaengig<Z extends Zustand>(verlauf: Verlauf<Z>): boolean {
  return verlauf.vergangenheit.length > 0;
}

export function kannWiederholen<Z extends Zustand>(verlauf: Verlauf<Z>): boolean {
  return verlauf.zukunft.length > 0;
}

export function rueckgaengig<Z extends Zustand>(
  verlauf: Verlauf<Z>,
  aktuell: Z,
): { verlauf: Verlauf<Z>; zustand: Z } | null {
  if (verlauf.vergangenheit.length === 0) return null;
  const zustand = verlauf.vergangenheit[verlauf.vergangenheit.length - 1];
  return {
    verlauf: {
      vergangenheit: verlauf.vergangenheit.slice(0, -1),
      zukunft: [...verlauf.zukunft, aktuell],
      letzter: null,
    },
    zustand,
  };
}

export function wiederholen<Z extends Zustand>(
  verlauf: Verlauf<Z>,
  aktuell: Z,
): { verlauf: Verlauf<Z>; zustand: Z } | null {
  if (verlauf.zukunft.length === 0) return null;
  const zustand = verlauf.zukunft[verlauf.zukunft.length - 1];
  return {
    verlauf: {
      vergangenheit: [...verlauf.vergangenheit, aktuell].slice(-MAX_SCHRITTE),
      zukunft: verlauf.zukunft.slice(0, -1),
      letzter: null,
    },
    zustand,
  };
}
